import * as React from "react";
import { ChevronDown } from "lucide-react";

import { SimpleMarkdown } from "./SimpleMarkdown";

interface ThoughtProcessContentProps {
  text: string;
  onCitationClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
}

type ToolKind = "finster" | "web" | "image";

interface ToolSection {
  kind: ToolKind;
  label: string;
  body: string;
}

interface ParsedThoughtProcess {
  reasoning: string;
  tools: ToolSection[];
}

const TOOL_HEADER_RE = /^\*\*(.+?)\*\*\s*$/gm;

function classifyToolHeader(rawLabel: string): { kind: ToolKind; label: string } | null {
  const label = rawLabel.replace(/:$/, "").trim();
  const normalized = label.toLowerCase();
  if (normalized === "finster data" || normalized === "factset ai") {
    return { kind: "finster", label };
  }
  if (normalized === "web search") {
    return { kind: "web", label };
  }
  if (normalized.startsWith("image tool used")) {
    return { kind: "image", label: "Image tool" };
  }
  return null;
}

function parseThoughtProcess(text: string): ParsedThoughtProcess {
  const matches: Array<{ label: string; text: string; index: number }> = [];
  let match: RegExpExecArray | null;
  TOOL_HEADER_RE.lastIndex = 0;
  while ((match = TOOL_HEADER_RE.exec(text)) !== null) {
    matches.push({
      label: match[1] ?? "",
      text: match[0],
      index: match.index,
    });
  }
  if (matches.length === 0) {
    return { reasoning: text.trim(), tools: [] };
  }

  const tools: ToolSection[] = [];
  let reasoningEnd = text.length;
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const header = classifyToolHeader(match.label);
    if (!header) continue;

    const start = match.index;
    const bodyStart = start + match.text.length;
    const nextStart = matches
      .slice(i + 1)
      .find((candidate) => classifyToolHeader(candidate.label))?.index ?? text.length;

    reasoningEnd = Math.min(reasoningEnd, start);
    const body = text.slice(bodyStart, nextStart).trim();
    if (body) {
      tools.push({ ...header, body });
    }
  }

  if (tools.length === 0) {
    return { reasoning: text.trim(), tools: [] };
  }

  return {
    reasoning: text.slice(0, reasoningEnd).trim(),
    tools,
  };
}

function getToolChrome(kind: ToolKind): {
  marker: string;
  accent: string;
} {
  switch (kind) {
    case "finster":
      return {
        marker: "bg-primary",
        accent: "text-primary",
      };
    case "web":
      return {
        marker: "bg-info",
        accent: "text-info",
      };
    case "image":
      return {
        marker: "bg-warning",
        accent: "text-warning",
      };
  }
}

function getPreviewText(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

const ToolResultSection: React.FC<{
  section: ToolSection;
  onCitationClick?: ThoughtProcessContentProps["onCitationClick"];
}> = ({ section, onCitationClick }) => {
  const [open, setOpen] = React.useState(true);
  const chrome = getToolChrome(section.kind);
  const preview = React.useMemo(() => getPreviewText(section.body), [section.body]);

  return (
    <section className="overflow-hidden rounded-lg border border-border/50 bg-background">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center gap-2 border-0 border-b border-border/40 bg-transparent px-3 py-2 text-left transition-colors hover:bg-muted/25"
        aria-expanded={open}
      >
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${chrome.marker}`} aria-hidden />
        <span className={`min-w-0 flex-1 truncate text-[12px] font-semibold ${chrome.accent}`}>
          {section.label}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {!open && preview && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block w-full cursor-pointer border-0 bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-muted/20"
        >
          <span className="line-clamp-2 text-[11.5px] leading-relaxed text-muted-foreground">
            {preview}
          </span>
          <span className={`mt-1.5 inline-flex text-[10px] font-semibold ${chrome.accent}`}>
            Show details
          </span>
        </button>
      )}

      {open && (
        <div className="px-3 py-2.5 text-foreground/80">
          <SimpleMarkdown text={section.body} compact onCitationClick={onCitationClick} />
        </div>
      )}
    </section>
  );
};

export const ThoughtProcessContent: React.FC<ThoughtProcessContentProps> = ({
  text,
  onCitationClick,
}) => {
  const parsed = React.useMemo(() => parseThoughtProcess(text), [text]);

  if (!parsed.reasoning && parsed.tools.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 text-[12px] leading-relaxed">
      {parsed.reasoning && (
        <section className="rounded-lg border border-border/50 bg-background px-3 py-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Reasoning
          </div>
          <div className="text-foreground/75">
            <SimpleMarkdown
              text={parsed.reasoning}
              className="thought-process-markdown"
              compact
              onCitationClick={onCitationClick}
            />
          </div>
        </section>
      )}

      {parsed.tools.map((section, index) => (
        <ToolResultSection
          key={`${section.kind}-${index}`}
          section={section}
          onCitationClick={onCitationClick}
        />
      ))}
    </div>
  );
};
